import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Flag, CheckCircle } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useApi, money, fmtDate, errText } from "@/lib/api";
import { useCanEdit } from "@/components/app/TrialBanner";

const THIS_YEAR = new Date().getFullYear();
const fromIso = (s) => { if (!s) return undefined; const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const useTarget = () => {
  const api = useApi();
  const { data } = useQuery({ queryKey: ["forecast-target"], queryFn: () => api.get("/forecast/target") });
  return data;
};

export const TargetPicker = ({ target }) => {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const canEdit = useCanEdit();
  const save = async (t) => {
    try {
      qc.setQueryData(["forecast-target"], await api.put("/forecast/target", { target: t }));
      toast.success(t ? `Target set to ${fmtDate(t)}` : "Target cleared");
      setOpen(false);
    } catch (e) { toast.error(errText(e, "Couldn't save the target")); }
  };
  if (!canEdit) return target?.target ? (
    <span data-testid="target-zcd-readonly" className="flex items-center gap-1 rounded-full border border-[#FFB800]/60 h-8 px-3 text-[11px] text-[#FFD166]"><Flag size={12} weight="fill" /> Target {fmtDate(target.target)}</span>
  ) : null;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setValue(target?.target || ""); }}>
      <PopoverTrigger asChild>
        <button data-testid="target-zcd-btn" className={`flex items-center gap-1 rounded-full border h-8 px-3 text-[11px] transition-colors ${target?.target ? "border-[#FFB800]/60 text-[#FFD166]" : "border-[#2A2A2A] text-[#A3A3A3] hover:border-[#FFB800] hover:text-[#FFD166]"}`}>
          <Flag size={12} weight={target?.target ? "fill" : "regular"} /> {target?.target ? `Target ${fmtDate(target.target)}` : "Set target"}
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid="target-zcd-popover" className="w-72 bg-[#121212] border-[#2A2A2A] text-white">
        <p className="text-sm font-medium">Target Zero Cash Date</p>
        <p className="text-[11px] text-[#A3A3A3] mt-1">The date you want your cash to last until, e.g. your next raise plus a buffer.</p>
        <div data-testid="target-zcd-input" className="mt-3 rounded-lg bg-[#050505] border border-[#2A2A2A]">
          <Calendar mode="single" selected={fromIso(value)} defaultMonth={fromIso(value) || new Date()} onSelect={(d) => setValue(d ? toIso(d) : "")}
            fromDate={new Date()} captionLayout="dropdown-buttons" fromYear={THIS_YEAR} toYear={THIS_YEAR + 8}
            classNames={{ caption_label: "hidden", vhidden: "sr-only", caption_dropdowns: "flex gap-2 px-8", dropdown: "rounded-md bg-[#121212] border border-[#2A2A2A] px-1 py-0.5 text-xs text-white",
              day_selected: "!bg-[#FFB800] !text-[#050505] hover:!bg-[#FFB800] focus:!bg-[#FFB800]", day_today: "border border-[#FFB800]/40" }} />
        </div>
        {value && <p data-testid="target-zcd-selected" className="mt-2 font-mono text-[11px] text-[#FFD166]">Selected: {fmtDate(value)}</p>}
        <div className="mt-3 flex gap-2">
          <button data-testid="target-zcd-save" onClick={() => save(value)} disabled={!value}
            className="flex-1 rounded-full bg-[#FFB800] py-2 text-xs font-semibold text-[#050505] disabled:opacity-40">Save target</button>
          {target?.target && <button data-testid="target-zcd-clear" onClick={() => save(null)} className="rounded-full border border-[#2A2A2A] px-3 text-xs hover:border-white">Clear</button>}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const TargetGap = ({ target }) => {
  if (!target?.target) return null;
  if (target.on_track) {
    return (
      <p data-testid="target-gap" className="mt-3 flex items-center gap-2 text-sm text-[#00FF9D]">
        <CheckCircle weight="fill" size={16} /> On track: your cash lasts past your {fmtDate(target.target)} target
        {target.gap_months != null && target.gap_months < 0 && <> by {Math.abs(target.gap_months).toFixed(1)} months</>}.
      </p>
    );
  }
  const c = money(target.monthly_change_needed, true);
  return (
    <p data-testid="target-gap" className="mt-3 flex items-start gap-2 text-sm text-[#FFD166]">
      <Flag weight="fill" size={16} className="mt-0.5 shrink-0" />
      <span><span className="font-medium">{target.gap_months.toFixed(1)} months short</span> of your {fmtDate(target.target)} target.
        To close it, cut about <span data-testid="target-gap-change" className="font-mono">{c}/mo</span> of burn or add about {c}/mo of MRR.
        <span className="ml-1 text-[11px] text-[#6B6B6B]">Estimate from today's cash; ignores growth.</span></span>
    </p>
  );
};
